/* SIZESOFTX
=========================================================================================
* Descripción: Landing pública móvil para campañas promocionales ARSync y captura CRM.
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1        | 14/09/2026 | José Luis Aragón   Cervantes     | Creación FASE 1 CRM.
* ---------------------------------------------------------------------------------------
*/

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CampaignIcon from "@mui/icons-material/Campaign";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SecurityIcon from "@mui/icons-material/Security";
import SendIcon from "@mui/icons-material/Send";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { apiCampaniaLead, apiCampaniaPublica, apiCampaniaVisita } from "../settings/supportApi";
import { serverSupport } from "../settings/ApiConfig";
import {
  esUrlSegura,
  normalizarImagenSegura,
  sanitizarEmail,
  sanitizarMultilinea,
  sanitizarTelefono,
  sanitizarTexto,
  tieneContenidoMalicioso,
  describirRiesgo,
  CODIGO_CAMPANIA_REGEX,
  EMAIL_REGEX,
} from "../settings/inputSecurity";

const CODIGO_REGEX = CODIGO_CAMPANIA_REGEX;

const LIMITES = {
  nombre: 100,
  nombreNegocio: 140,
  telefono: 25,
  whatsApp: 25,
  email: 150,
  ciudad: 100,
  estado: 100,
  mensaje: 700,
};

const formularioInicial = {
  nombre: "",
  nombreNegocio: "",
  telefono: "",
  whatsApp: "",
  email: "",
  ciudad: "",
  estado: "",
  mensaje: "",
  consentimientoContacto: false,
};

const safeStorageGet = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const safeStorageSet = (storage, key, value) => {
  try {
    storage.setItem(key, value);
  } catch {
    // Sin almacenamiento disponible, el backend conserva la protección por IP y duplicados.
  }
};

const crearIdCliente = () => {
  if (window.crypto && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  return `arsync-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const obtenerIdPersistente = (storage, key) => {
  const existente = safeStorageGet(storage, key);
  if (existente) {
    return existente;
  }

  const nuevo = crearIdCliente();
  safeStorageSet(storage, key, nuevo);
  return nuevo;
};

const limpiarTexto = (valor, limite, multilinea = false) =>
  multilinea ? sanitizarMultilinea(valor, limite) : sanitizarTexto(valor, limite);

const normalizarTelefono = (valor) => sanitizarTelefono(valor, 25);

const obtenerParametro = (searchParams, nombre, limite = 120) => {
  return sanitizarTexto(searchParams.get(nombre), limite);
};

const extraerGoogleDriveId = (valor) => {
  const texto = String(valor || "").trim();
  if (tieneContenidoMalicioso(texto)) return null;
  const fileMatch = texto.match(/drive\.google\.com\/file\/d\/([^/\s?#]+)/i);
  if (fileMatch?.[1] && /^[A-Za-z0-9_-]{10,200}$/.test(fileMatch[1])) {
    return fileMatch[1];
  }

  const queryMatch = texto.match(/[?&]id=([^&\s#]+)/i);
  if (/drive\.google\.com/i.test(texto) && queryMatch?.[1] && /^[A-Za-z0-9_-]{10,200}$/.test(queryMatch[1])) {
    return queryMatch[1];
  }

  return null;
};

const normalizarImagenPublicaUrl = (valor, respaldo = false) => normalizarImagenSegura(valor, 700, respaldo);

// Endpoint binario directo: muestra la imagen guardada en BD aunque la URL
// absoluta configurada en el backend falte o sea inalcanzable.
const urlImagenBinaria = (codigo) => {
  const c = String(codigo || "").trim();
  if (!c || !serverSupport) return "";
  return `${String(serverSupport).replace(/\/$/, "")}/SupportCampaniasPublicas/${encodeURIComponent(c)}/imagen`;
};

// URLs de campaña (WhatsApp / video) solo se usan si son https seguras. Evita stored-XSS via javascript:/data:.
const obtenerUrlCampaniaSegura = (valor, limite = 700) => {
  const texto = sanitizarTexto(valor, limite).trim();
  if (!texto) return "";
  return esUrlSegura(texto) ? texto : "";
};

// Extrae el ID de un video de YouTube (watch, youtu.be, shorts, embed, live).
// El ID siempre se valida con la misma expresión para armar URLs de thumbnail/embed seguras.
const extraerIdYouTube = (valor) => {
  const texto = String(valor || "").trim();
  if (!texto || tieneContenidoMalicioso(texto)) return "";
  let url = null;
  try {
    url = new URL(texto);
  } catch {
    return "";
  }

  const host = url.hostname.replace(/^www\./i, "").toLowerCase();
  const esIdValido = (id) => /^[A-Za-z0-9_-]{11}$/.test(id || "");

  if (host === "youtu.be") {
    const id = url.pathname.split("/").filter(Boolean)[0] || "";
    return esIdValido(id) ? id : "";
  }

  const dominiosYouTube = ["youtube.com", "m.youtube.com", "music.youtube.com", "youtube-nocookie.com"];
  if (dominiosYouTube.includes(host)) {
    const desdeQuery = url.searchParams.get("v");
    if (esIdValido(desdeQuery)) return desdeQuery;

    const partes = url.pathname.split("/").filter(Boolean);
    const indice = partes.findIndex((parte) => ["embed", "shorts", "live", "v"].includes(parte));
    if (indice >= 0 && esIdValido(partes[indice + 1])) return partes[indice + 1];
  }

  return "";
};

const validarFormulario = (formulario) => {
  const errores = {};
  // Bloqueo de inyección: se revisa el valor crudo antes de sanitizar para no dejar pasar <script>, javascript:, on*=, etc.
  const camposTexto = [
    { etiqueta: "nombre", valor: formulario.nombre },
    { etiqueta: "negocio", valor: formulario.nombreNegocio },
    { etiqueta: "ciudad", valor: formulario.ciudad },
    { etiqueta: "estado", valor: formulario.estado },
    { etiqueta: "mensaje", valor: formulario.mensaje },
    { etiqueta: "correo", valor: formulario.email },
    { etiqueta: "teléfono", valor: formulario.telefono },
    { etiqueta: "whatsApp", valor: formulario.whatsApp },
  ];
  for (const c of camposTexto) {
    if (c.valor && tieneContenidoMalicioso(String(c.valor))) {
      errores[c.etiqueta] = `Contenido bloqueado por seguridad (${describirRiesgo(c.valor)}).`;
    }
  }
  const telefono = normalizarTelefono(formulario.telefono);
  const whatsApp = normalizarTelefono(formulario.whatsApp);
  const digitosTelefono = telefono.replace(/\D/g, "");
  const digitosWhatsApp = whatsApp.replace(/\D/g, "");

  if (limpiarTexto(formulario.nombre, LIMITES.nombre).length < 2) {
    errores.nombre = errores.nombre || "Escribe tu nombre completo.";
  }

  if (!digitosTelefono && !digitosWhatsApp) {
    errores.telefono = "Indica teléfono o WhatsApp.";
    errores.whatsApp = "Indica teléfono o WhatsApp.";
  }

  if (digitosTelefono && (digitosTelefono.length < 8 || digitosTelefono.length > 15)) {
    errores.telefono = "Usa entre 8 y 15 dígitos.";
  }

  if (digitosWhatsApp && (digitosWhatsApp.length < 8 || digitosWhatsApp.length > 15)) {
    errores.whatsApp = "Usa entre 8 y 15 dígitos.";
  }

  const email = limpiarTexto(formulario.email, LIMITES.email).toLowerCase();
  if (email && !EMAIL_REGEX.test(email)) {
    errores.email = "Correo inválido.";
  }

  if (!formulario.consentimientoContacto) {
    errores.consentimientoContacto = "Necesitamos tu autorización para contactarte.";
  }

  return errores;
};

const normalizarFormulario = (formulario, productoServicio) => ({
  nombre: limpiarTexto(formulario.nombre, LIMITES.nombre),
  nombreNegocio: limpiarTexto(formulario.nombreNegocio, LIMITES.nombreNegocio),
  telefono: normalizarTelefono(formulario.telefono),
  whatsApp: normalizarTelefono(formulario.whatsApp),
  email: sanitizarEmail(formulario.email, LIMITES.email),
  ciudad: limpiarTexto(formulario.ciudad, LIMITES.ciudad),
  estado: limpiarTexto(formulario.estado, LIMITES.estado),
  productoInteres: limpiarTexto(productoServicio, 120),
  mensaje: limpiarTexto(formulario.mensaje, LIMITES.mensaje, true),
  consentimientoContacto: formulario.consentimientoContacto,
});

const PromoCampaniaPage = () => {
  const { codigo } = useParams();
  const [searchParams] = useSearchParams();
  const codigoNormalizado = (codigo || "").trim().toUpperCase();
  const [campania, setCampania] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState("");
  const [formulario, setFormulario] = useState(formularioInicial);
  const [errores, setErrores] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [respuesta, setRespuesta] = useState(null);
  const [errorEnvio, setErrorEnvio] = useState("");
  const [videoDemoIniciado, setVideoDemoIniciado] = useState(false);

  const sessionId = useMemo(() => obtenerIdPersistente(window.sessionStorage, "arsync_promo_session_id"), []);
  const visitorId = useMemo(() => obtenerIdPersistente(window.localStorage, "arsync_promo_visitor_id"), []);
  const envioKey = useMemo(() => `arsync_promo_lead_${codigoNormalizado}_${sessionId}`, [codigoNormalizado, sessionId]);

  const origen = useMemo(() => {
    const canal = obtenerParametro(searchParams, "canal", 30)
      || obtenerParametro(searchParams, "utm_source", 30)
      || campania?.canal
      || "WEB";

    return {
      sessionId,
      visitorId,
      canal: canal.toUpperCase(),
      urlOrigen: limpiarTexto(window.location.href, 500),
      referer: limpiarTexto(document.referrer, 500),
      utmSource: obtenerParametro(searchParams, "utm_source"),
      utmMedium: obtenerParametro(searchParams, "utm_medium"),
      utmCampaign: obtenerParametro(searchParams, "utm_campaign"),
      utmContent: obtenerParametro(searchParams, "utm_content"),
      utmTerm: obtenerParametro(searchParams, "utm_term"),
    };
  }, [campania?.canal, searchParams, sessionId, visitorId]);

  useEffect(() => {
    const guardado = safeStorageGet(window.sessionStorage, envioKey);
    if (!guardado) {
      setRespuesta(null);
      return;
    }

    try {
      setRespuesta(JSON.parse(guardado));
    } catch {
      setRespuesta(null);
    }
  }, [envioKey]);

  useEffect(() => {
    let activo = true;

    const cargarCampania = async () => {
      if (!CODIGO_REGEX.test(codigoNormalizado)) {
        setCargando(false);
        setErrorCarga("La campaña solicitada no es válida.");
        return;
      }

      try {
        setCargando(true);
        setErrorCarga("");
        const data = await apiCampaniaPublica(codigoNormalizado);
        if (activo) {
          setVideoDemoIniciado(false);
          setCampania(data);
        }
      } catch (error) {
        if (activo) {
          setErrorCarga(error.message || "No pudimos cargar esta campaña.");
        }
      } finally {
        if (activo) {
          setCargando(false);
        }
      }
    };

    cargarCampania();

    return () => {
      activo = false;
    };
  }, [codigoNormalizado]);

  useEffect(() => {
    if (!campania) {
      return;
    }

    const tituloAnterior = document.title;
    document.title = `${campania.titulo || campania.nombre || "Campaña"} | ARSync`;

    return () => {
      document.title = tituloAnterior;
    };
  }, [campania]);

  useEffect(() => {
    if (!campania || !codigoNormalizado) {
      return;
    }

    const visitaKey = `arsync_promo_visit_${codigoNormalizado}_${sessionId}`;
    if (safeStorageGet(window.sessionStorage, visitaKey)) {
      return;
    }

    apiCampaniaVisita(codigoNormalizado, origen)
      .then(() => {
        safeStorageSet(window.sessionStorage, visitaKey, "1");
      })
      .catch(() => {
        // La visita no bloquea la captura del prospecto.
      });
  }, [campania, codigoNormalizado, origen, sessionId]);

  const actualizarCampo = useCallback((event) => {
    const { name, value, checked, type } = event.target;
    setFormulario((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value.slice(0, LIMITES[name] || 200),
    }));
    setErrores((prev) => ({ ...prev, [name]: undefined }));
    setErrorEnvio("");
  }, []);

  const registrarProspecto = async (event) => {
    event.preventDefault();
    if (respuesta) {
      return;
    }

    const erroresFormulario = validarFormulario(formulario);
    setErrores(erroresFormulario);
    if (Object.keys(erroresFormulario).length > 0) {
      return;
    }

    const payload = {
      ...origen,
      ...normalizarFormulario(formulario, campania?.productoServicio || "ARSync"),
    };

    try {
      setEnviando(true);
      setErrorEnvio("");
      const data = await apiCampaniaLead(codigoNormalizado, payload);
      setRespuesta(data);
      safeStorageSet(window.sessionStorage, envioKey, JSON.stringify(data));
    } catch (error) {
      setErrorEnvio(error.message || "No pudimos registrar tu solicitud. Intenta nuevamente.");
    } finally {
      setEnviando(false);
    }
  };

  if (cargando) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", bgcolor: "#07111f", color: "white" }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress color="inherit" />
          <Typography>Cargando campaña ARSync...</Typography>
        </Stack>
      </Box>
    );
  }

  if (errorCarga) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", bgcolor: "#07111f", p: 2 }}>
        <Paper sx={{ maxWidth: 520, p: 4, borderRadius: 4, textAlign: "center" }}>
          <Chip icon={<CampaignIcon />} label="ARSync" color="primary" sx={{ mb: 2, fontWeight: 700 }} />
          <Typography variant="h5" fontWeight={800} gutterBottom>
            Campaña no disponible
          </Typography>
          <Typography color="text.secondary">{errorCarga}</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(160deg, #07111f 0%, #0f2544 45%, #f5f7fb 45%, #ffffff 100%)",
        py: { xs: 2, md: 6 },
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ color: "white" }}>
            <Stack direction="row" alignItems="center" spacing={1.2}>
              <Box sx={{ bgcolor: "primary.main", width: 42, height: 42, borderRadius: 2, display: "grid", placeItems: "center" }}>
                <CampaignIcon />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={900} lineHeight={1}>
                  ARSync
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Promocionales SizeSoftX
                </Typography>
              </Box>
            </Stack>
            <Chip label={campania?.canal || "WEB"} color="info" size="small" sx={{ fontWeight: 700 }} />
          </Stack>

          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} md={7}>
              <Paper
                elevation={0}
                sx={{
                  height: "100%",
                  p: { xs: 3, md: 5 },
                  borderRadius: 5,
                  overflow: "hidden",
                  bgcolor: "rgba(255,255,255,0.96)",
                }}
              >
                <Stack spacing={3}>
                  <Chip
                    icon={<SecurityIcon />}
                    label="Captura segura de prospectos"
                    color="success"
                    sx={{ width: "fit-content", fontWeight: 700 }}
                  />

                  <Box>
                    <Typography variant="h3" fontWeight={900} sx={{ fontSize: { xs: 34, md: 54 }, lineHeight: 1.04 }}>
                      {campania?.titulo || "Solicita tu demo ARSync"}
                    </Typography>
                    {campania?.subtitulo && (
                      <Typography variant="h6" color="text.secondary" sx={{ mt: 2, lineHeight: 1.35 }}>
                        {campania.subtitulo}
                      </Typography>
                    )}
                  </Box>

                  {campania?.descripcionComercial && (
                    <Typography color="text.secondary" sx={{ fontSize: 17, lineHeight: 1.65 }}>
                      {campania.descripcionComercial}
                    </Typography>
                  )}

                  {(() => {
                    const imgSegura = normalizarImagenPublicaUrl(campania?.imagenUrl)
                      || (campania?.tieneImagenArchivo ? urlImagenBinaria(campania?.codigo) : "");
                    if (!imgSegura) return null;
                    const respaldoBinario = urlImagenBinaria(campania?.codigo);
                    return (
                      <Box
                        component="img"
                        src={imgSegura}
                        alt={sanitizarTexto(campania.titulo || "Campaña ARSync", 180)}
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        onError={(event) => {
                          const fallback = normalizarImagenPublicaUrl(campania.imagenUrl, true);
                          if (fallback && event.currentTarget.src !== fallback) {
                            event.currentTarget.src = fallback;
                            return;
                          }
                          if (respaldoBinario && event.currentTarget.src !== respaldoBinario) {
                            event.currentTarget.src = respaldoBinario;
                            return;
                          }
                          event.currentTarget.style.display = "none";
                        }}
                        sx={{ width: "100%", maxHeight: 360, objectFit: "cover", borderRadius: 4, bgcolor: "#eef2f7" }}
                      />
                    );
                  })()}

                  {campania?.beneficios?.length > 0 && (
                    <Stack spacing={1.4}>
                      {campania.beneficios.map((beneficio) => (
                        <Stack key={beneficio.id || beneficio.orden} direction="row" spacing={1.2} alignItems="flex-start">
                          <CheckCircleOutlineIcon color="success" fontSize="small" sx={{ mt: 0.3 }} />
                          <Typography>{beneficio.texto}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  )}

                  {(() => {
                    const idYouTube = extraerIdYouTube(obtenerUrlCampaniaSegura(campania?.videoUrl));
                    if (!idYouTube) return null;
                    return (
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          pt: "56.25%",
                          borderRadius: 4,
                          overflow: "hidden",
                          bgcolor: "#000",
                        }}
                      >
                        {videoDemoIniciado ? (
                          <Box
                            component="iframe"
                            src={`https://www.youtube-nocookie.com/embed/${idYouTube}?autoplay=1&rel=0`}
                            title="Demo del producto"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            sx={{
                              position: "absolute",
                              inset: 0,
                              width: "100%",
                              height: "100%",
                              border: 0,
                            }}
                          />
                        ) : (
                          <Box
                            component="button"
                            type="button"
                            aria-label="Reproducir video de demostración"
                            onClick={() => setVideoDemoIniciado(true)}
                            sx={{
                              position: "absolute",
                              inset: 0,
                              width: "100%",
                              height: "100%",
                              p: 0,
                              m: 0,
                              border: 0,
                              cursor: "pointer",
                              bgcolor: "#000",
                            }}
                          >
                            <Box
                              component="img"
                              src={`https://i.ytimg.com/vi/${idYouTube}/maxresdefault.jpg`}
                              alt=""
                              loading="lazy"
                              referrerPolicy="no-referrer"
                              onError={(event) => {
                                const img = event.currentTarget;
                                if (img.dataset.respaldo !== "1") {
                                  img.dataset.respaldo = "1";
                                  img.src = `https://i.ytimg.com/vi/${idYouTube}/hqdefault.jpg`;
                                  return;
                                }
                                img.style.display = "none";
                              }}
                              sx={{
                                position: "absolute",
                                inset: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                            <Box
                              sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Box
                                sx={{
                                  width: { xs: 64, md: 80 },
                                  height: { xs: 64, md: 80 },
                                  borderRadius: "50%",
                                  bgcolor: "rgba(0,0,0,0.62)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "transform 0.15s ease",
                                  "&:hover": { transform: "scale(1.08)" },
                                }}
                              >
                                <PlayArrowIcon sx={{ color: "#fff", fontSize: { xs: 40, md: 48 } }} />
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    );
                  })()}

                  <Divider />

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    {(() => {
                      const waSegura = obtenerUrlCampaniaSegura(campania?.whatsAppUrl);
                      if (!waSegura) return null;
                      return (
                        <Button
                          component={Link}
                          href={waSegura}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="contained"
                          color="success"
                          size="large"
                          startIcon={<WhatsAppIcon />}
                          sx={{ textDecoration: "none", fontWeight: 800 }}
                        >
                          WhatsApp
                        </Button>
                      );
                    })()}
                    {(() => {
                      const videoSeguro = obtenerUrlCampaniaSegura(campania?.videoUrl);
                      // Si es YouTube ya se muestra el reproductor integrado; aquí queda solo
                      // el enlace de respaldo para videos con otro origen (ej. Google Drive).
                      if (!videoSeguro || extraerIdYouTube(videoSeguro)) return null;
                      return (
                        <Button
                          component={Link}
                          href={videoSeguro}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="outlined"
                          size="large"
                          endIcon={<ArrowForwardIcon />}
                          sx={{ textDecoration: "none", fontWeight: 800 }}
                        >
                          Ver demo
                        </Button>
                      );
                    })()}
                  </Stack>
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 5 }}>
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="h5" fontWeight={900}>
                      {campania?.ctaTexto || "Solicitar demo"}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 0.8 }}>
                      Llena tus datos y te daremos un ID de prospecto para seguimiento.
                    </Typography>
                  </Box>

                  {respuesta && (
                    <Alert severity="success" icon={<CheckCircleOutlineIcon />}>
                      {respuesta.mensaje || "Solicitud recibida."}
                      <br />
                      ID de prospecto: <strong>{respuesta.codigoProspecto}</strong>
                    </Alert>
                  )}

                  {errorEnvio && <Alert severity="error">{errorEnvio}</Alert>}

                  {campania?.mostrarFormulario ? (
                    <Box component="form" onSubmit={registrarProspecto} noValidate>
                      <Stack spacing={2}>
                        <TextField
                          name="nombre"
                          label="Nombre completo"
                          value={formulario.nombre}
                          onChange={actualizarCampo}
                          required
                          fullWidth
                          inputProps={{ maxLength: LIMITES.nombre }}
                          error={Boolean(errores.nombre)}
                          helperText={errores.nombre}
                          disabled={Boolean(respuesta)}
                        />
                        <TextField
                          name="nombreNegocio"
                          label="Negocio o empresa"
                          value={formulario.nombreNegocio}
                          onChange={actualizarCampo}
                          fullWidth
                          inputProps={{ maxLength: LIMITES.nombreNegocio }}
                          disabled={Boolean(respuesta)}
                        />
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              name="whatsApp"
                              label="WhatsApp"
                              value={formulario.whatsApp}
                              onChange={actualizarCampo}
                              fullWidth
                              inputProps={{ maxLength: LIMITES.whatsApp, inputMode: "tel" }}
                              error={Boolean(errores.whatsApp)}
                              helperText={errores.whatsApp}
                              disabled={Boolean(respuesta)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              name="telefono"
                              label="Teléfono"
                              value={formulario.telefono}
                              onChange={actualizarCampo}
                              fullWidth
                              inputProps={{ maxLength: LIMITES.telefono, inputMode: "tel" }}
                              error={Boolean(errores.telefono)}
                              helperText={errores.telefono}
                              disabled={Boolean(respuesta)}
                            />
                          </Grid>
                        </Grid>
                        <TextField
                          name="email"
                          label="Correo"
                          value={formulario.email}
                          onChange={actualizarCampo}
                          fullWidth
                          inputProps={{ maxLength: LIMITES.email, inputMode: "email" }}
                          error={Boolean(errores.email)}
                          helperText={errores.email}
                          disabled={Boolean(respuesta)}
                        />
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              name="ciudad"
                              label="Ciudad"
                              value={formulario.ciudad}
                              onChange={actualizarCampo}
                              fullWidth
                              inputProps={{ maxLength: LIMITES.ciudad }}
                              disabled={Boolean(respuesta)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              name="estado"
                              label="Estado"
                              value={formulario.estado}
                              onChange={actualizarCampo}
                              fullWidth
                              inputProps={{ maxLength: LIMITES.estado }}
                              disabled={Boolean(respuesta)}
                            />
                          </Grid>
                        </Grid>
                        <TextField
                          name="mensaje"
                          label="¿Qué necesitas probar?"
                          value={formulario.mensaje}
                          onChange={actualizarCampo}
                          fullWidth
                          multiline
                          minRows={3}
                          inputProps={{ maxLength: LIMITES.mensaje }}
                          disabled={Boolean(respuesta)}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="consentimientoContacto"
                              checked={formulario.consentimientoContacto}
                              onChange={actualizarCampo}
                              disabled={Boolean(respuesta)}
                            />
                          }
                          label="Acepto que ARSync me contacte para seguimiento de esta solicitud."
                        />
                        {errores.consentimientoContacto && (
                          <Typography color="error" variant="caption">
                            {errores.consentimientoContacto}
                          </Typography>
                        )}
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          startIcon={enviando ? <CircularProgress color="inherit" size={18} /> : <SendIcon />}
                          disabled={enviando || Boolean(respuesta)}
                          sx={{ py: 1.4, fontWeight: 900, borderRadius: 3 }}
                        >
                          {respuesta ? "Solicitud registrada" : "Registrar prospecto"}
                        </Button>
                      </Stack>
                    </Box>
                  ) : (
                    <Alert severity="info">
                      Esta campaña no tiene formulario activo. Usa los canales de contacto publicados por ARSync.
                    </Alert>
                  )}

                  <Typography variant="caption" color="text.secondary">
                    ARSync protege esta captura con validación, límite de longitud y registro de origen de campaña.
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      </Container>
    </Box>
  );
};

export default PromoCampaniaPage;
